/**
 * ProductCard.js — Qty stepper with direct input field for large quantities
 */
import React, { useState, useRef } from 'react';
import { Link }     from 'react-router-dom';
import { useCart }  from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { getImageUrl, getImageUrlFallback } from '../../services/api';

const UNIT_LABELS = {
  box: 'Box', pkt: 'Pkt', piece: 'Piece',
  set: 'Set', dozen: 'Dozen', bundle: 'Bundle', roll: 'Roll',
};

export default function ProductCard({ product, index = 0 }) {
  const { addItem, isInCart } = useCart();
  const { toast }             = useToast();
  const [qty,    setQty]    = useState(1);
  const [adding, setAdding] = useState(false);
  const [tilt,   setTilt]   = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const inCart   = isInCart(product.id);
  const imageUrl = getImageUrl(product);
  const fallback = getImageUrlFallback();
  const maxQty   = Math.min(product.stock, 250);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width  - 0.5) * 8;
    const y = ((e.clientY - rect.top)  / rect.height - 0.5) * -8;
    setTilt({ x, y });
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  // Handle direct input — clamp to valid range on blur
  const handleQtyInput = (e) => {
    const raw = e.target.value;
    if (raw === '') { setQty(''); return; }
    const n = parseInt(raw, 10);
    if (!isNaN(n)) setQty(n);
  };
  const handleQtyBlur = () => {
    const n = parseInt(qty, 10);
    if (isNaN(n) || n < 1) setQty(1);
    else if (n > maxQty) setQty(maxQty);
    else setQty(n);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!product.in_stock || adding) return;
    const finalQty = Math.min(Math.max(1, parseInt(qty, 10) || 1), maxQty);
    setQty(finalQty);
    setAdding(true);
    addItem(product, finalQty);

    const emojis = ['✨','🎆','⭐','💥','🌟'];
    for (let i = 0; i < 5; i++) {
      const el = document.createElement('div');
      el.className = 'sparkle-particle';
      el.textContent = emojis[i];
      el.style.left = `${e.clientX + (Math.random()-0.5)*40}px`;
      el.style.top  = `${e.clientY}px`;
      el.style.animationDelay = `${i * 0.08}s`;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 900);
    }
    toast.success(`✨ ${finalQty} × "${product.name}" added!`);
    setTimeout(() => { setAdding(false); }, 900);
  };

  const stockLevel = product.stock === 0 ? 'out'
    : product.stock <= 5  ? 'critical'
    : product.stock <= 10 ? 'low'
    : 'ok';

  return (
    <Link to={`/products/${product.slug}`}
      className="group block animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}>
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: `perspective(800px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
          transition: tilt.x === 0 ? 'transform 0.5s ease' : 'transform 0.1s ease',
        }}
        className="bg-white rounded-2xl overflow-hidden
          shadow-[0_2px_12px_rgba(0,0,0,0.07)]
          hover:shadow-[0_20px_48px_rgba(192,21,42,0.18)]
          border border-gray-100/80 hover:border-yellow-300/60
          transition-shadow duration-300">

        {/* Image */}
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 h-44 overflow-hidden">
          <img src={imageUrl} alt={product.name}
            onError={e => { e.target.src = fallback; }}
            loading="lazy" decoding="async" width="400" height="176"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"/>
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent
            opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"/>

          {/* Badges */}
          <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
            {product.discount_percent > 0 && (
              <span className="bg-gradient-to-br from-red-600 to-red-700 text-white text-[10px] font-black
                px-2.5 py-0.5 rounded-full shadow-lg shadow-red-600/30">
                {product.discount_percent}% OFF
              </span>
            )}
            {product.is_featured === 1 && (
              <span className="bg-gradient-to-br from-yellow-400 to-amber-400 text-red-900 text-[10px] font-black
                px-2.5 py-0.5 rounded-full shadow-lg shadow-yellow-400/30">
                ⭐ FEATURED
              </span>
            )}
          </div>

          {!product.in_stock && (
            <div className="absolute inset-0 bg-gray-900/55 backdrop-blur-[1px] flex items-center justify-center">
              <span className="bg-white text-red-700 font-bold text-xs px-4 py-1.5 rounded-full shadow-lg">
                Out of Stock
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <h3 className="font-bold text-gray-800 text-sm leading-snug mb-1.5 line-clamp-2
            group-hover:text-red-700 transition-colors duration-200">
            {product.name}
          </h3>

          {/* Price */}
          <div className="flex items-baseline gap-1.5 mb-1.5">
            <span className="font-black text-base text-gray-900"
              style={{ fontFamily: '"Plus Jakarta Sans",sans-serif', letterSpacing: '-0.02em' }}>
              ₹{product.price}
            </span>
            {product.original_price && (
              <span className="text-gray-400 text-xs line-through">₹{product.original_price}</span>
            )}
            {product.discount_percent > 0 && (
              <span className="text-green-600 text-[10px] font-black bg-green-50 px-1 py-0.5 rounded-md">
                -{product.discount_percent}%
              </span>
            )}
          </div>

          {/* Unit type badge */}
          {product.unit_type && (
            <div className="mb-1.5">
              <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200/70
                text-amber-700 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide">
                <svg className="w-2.5 h-2.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 3a1 1 0 00-1 1v1h14V4a1 1 0 00-1-1H4zm-1 4v9a1 1 0 001 1h12a1 1 0 001-1V7H3z"/>
                </svg>
                {UNIT_LABELS[product.unit_type] || product.unit_type}
              </span>
            </div>
          )}

          {/* Stock */}
          <div className="flex items-center gap-1 mb-2.5">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              stockLevel === 'ok'       ? 'bg-green-500' :
              stockLevel === 'low'      ? 'bg-amber-500 animate-pulse' :
              stockLevel === 'critical' ? 'bg-orange-500 animate-pulse' :
              'bg-red-500'
            }`}/>
            <span className={`text-[10px] font-semibold ${
              stockLevel === 'ok' ? 'text-gray-400' :
              stockLevel === 'low' ? 'text-amber-600' :
              stockLevel === 'critical' ? 'text-orange-600' : 'text-red-500'
            }`}>
              {stockLevel === 'ok' ? 'In Stock' :
               stockLevel === 'low' ? `Only ${product.stock} left` :
               stockLevel === 'critical' ? `Last ${product.stock}!` : 'Out of Stock'}
            </span>
          </div>

          {/* Qty + Add to Cart */}
          {product.in_stock ? (
            <div className="flex gap-1.5 items-center" onClick={e => e.preventDefault()}>
              {/* Stepper with editable input */}
              <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden flex-shrink-0 bg-gray-50">
                {/* Decrement */}
                <button
                  onClick={e => { e.preventDefault(); setQty(q => Math.max(1, (parseInt(q,10)||1) - 1)); }}
                  className="w-7 h-8 flex items-center justify-center text-gray-600
                    hover:bg-red-50 hover:text-red-600 font-bold transition-colors text-sm">
                  −
                </button>

                {/* Editable number input */}
                <input
                  type="number"
                  min={1}
                  max={maxQty}
                  value={qty}
                  onChange={handleQtyInput}
                  onBlur={handleQtyBlur}
                  onClick={e => { e.preventDefault(); e.target.select(); }}
                  className="w-9 h-8 text-center font-black text-gray-800 text-sm bg-transparent
                    border-none outline-none focus:outline-none focus:ring-0
                    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
                    [&::-webkit-inner-spin-button]:appearance-none"
                />

                {/* Increment */}
                <button
                  onClick={e => { e.preventDefault(); setQty(q => Math.min(maxQty, (parseInt(q,10)||1) + 1)); }}
                  disabled={(parseInt(qty,10)||1) >= maxQty}
                  className="w-7 h-8 flex items-center justify-center text-gray-600
                    hover:bg-red-50 hover:text-red-600 font-bold transition-colors text-sm
                    disabled:opacity-30 disabled:cursor-not-allowed">
                  +
                </button>
              </div>

              {/* Add button */}
              <button
                onClick={handleAdd}
                disabled={adding}
                className={`flex-1 py-2 rounded-xl font-bold text-xs
                  transition-all duration-300 relative overflow-hidden
                  ${adding
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white scale-95'
                    : inCart
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                    : 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-md shadow-red-600/25 hover:shadow-red-600/40 hover:-translate-y-0.5 active:scale-95'
                  }`}>
                {adding
                  ? <span className="flex items-center justify-center gap-1">
                      <span className="animate-spin text-xs">✦</span> Adding…
                    </span>
                  : inCart ? '✓ In Cart'
                  : '🛒 Add'}
              </button>
            </div>
          ) : (
            <button disabled
              className="w-full py-2.5 rounded-xl font-bold text-xs bg-gray-100 text-gray-400 cursor-not-allowed">
              Unavailable
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
